import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useQuery } from "@tanstack/react-query";
import useDebounce from "hooks/useDebounce";
import useResettableState from "hooks/useResettableState";
import { useToast } from "components/Toast";
import { CustomerType, getDashboardCustomers } from "services/analytics";

// The API caps limit at 100.
const PAGE_SIZES = [5, 10, 20, 50, 100];

const formatDate = (value: string) => {
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const headCellSx = {
  bgcolor: "#F2F4F7",
  color: "#475467",
  fontSize: 12,
  fontWeight: 600,
  borderBottom: "none",
  whiteSpace: "nowrap",
};

const typeChip = {
  new: { label: "New", bgcolor: "#EBF1FF", color: "#3E7EFF" },
  returning: { label: "Returning", bgcolor: "#ECFDF3", color: "#379E66" },
};

const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const { showToast } = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      showToast(`${label} copied to clipboard!`, "success");
    } catch {
      showToast(`Failed to copy ${label.toLowerCase()}`, "error");
    }
  };

  return (
    <Tooltip title={`Copy ${label.toLowerCase()}`}>
      <IconButton size="small" onClick={handleCopy} sx={{ p: "2px", color: "#98A2B3" }}>
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
};

const CustomersTable = () => {
  const [type, setType] = useState<CustomerType>("all");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const debouncedSearch = useDebounce(search.trim(), 400);

  // Reset to the first page whenever the filters change.
  const [page, setPage] = useResettableState(
    1,
    [type, debouncedSearch, limit].join("|"),
  );

  const params = { type, search: debouncedSearch, page, limit };

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["dashboard", "customers", params],
    queryFn: () => getDashboardCustomers(params),
    placeholderData: (prev) => prev,
    staleTime: 60 * 1000,
  });

  const customers = data?.data?.customers || [];
  const counts = data?.data?.counts;
  const pagination = data?.data?.pagination;
  // Fall back to the count for the active tab if the API omits pagination.
  const totalItems =
    pagination?.total ?? (counts ? (type === "all" ? counts.total : counts[type]) : 0);
  const totalPages =
    pagination?.pages ??
    pagination?.totalPages ??
    Math.max(1, Math.ceil(totalItems / limit));

  const tabLabel = (label: string, count?: number) =>
    count === undefined ? label : `${label} (${count.toLocaleString()})`;

  return (
    <Box mt={5}>
      <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <CardContent>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            gap="12px"
            borderBottom="1px solid #E4E7EC"
            pb={2}
            mb={3}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: 18, sm: 20 } }}
              >
                Flight Customers
              </Typography>
              <Typography variant="body2">
                All-time list of new and returning flight customers
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              gap="12px"
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <TextField
                size="small"
                placeholder="Search name, email, phone, PNR..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: { sm: 260 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#98A2B3" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={type}
                onChange={(_, v) => v && setType(v)}
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "none",
                    textTransform: "none",
                    px: "16px",
                    flex: { xs: 1, sm: "initial" },
                    color: "#475467",
                    whiteSpace: "nowrap",
                    borderRadius: "6px !important",
                  },
                  "& .Mui-selected": {
                    bgcolor: "#F2F4F7 !important",
                    color: "#101828 !important",
                    fontWeight: 600,
                  },
                }}
              >
                <ToggleButton value="all">{tabLabel("All", counts?.total)}</ToggleButton>
                <ToggleButton value="new">{tabLabel("New", counts?.new)}</ToggleButton>
                <ToggleButton value="returning">
                  {tabLabel("Returning", counts?.returning)}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <TableContainer
            sx={{ overflowX: "auto", opacity: isFetching && !isLoading ? 0.6 : 1 }}
          >
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, borderRadius: "8px 0 0 8px" }}>
                    Customer
                  </TableCell>
                  <TableCell sx={headCellSx}>Phone</TableCell>
                  <TableCell sx={headCellSx}>Type</TableCell>
                  <TableCell sx={headCellSx} align="center">
                    Bookings
                  </TableCell>
                  <TableCell sx={headCellSx}>Last Route</TableCell>
                  <TableCell sx={headCellSx}>Last PNR</TableCell>
                  <TableCell sx={headCellSx}>First Booking</TableCell>
                  <TableCell sx={{ ...headCellSx, borderRadius: "0 8px 8px 0" }}>
                    Last Booking
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  [0, 1, 2, 3, 4].map((i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={8}>
                        <Skeleton height={28} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography textAlign="center" color="#98A2B3" fontSize={14} py="24px">
                        {isError ? "Failed to load customers" : "No customers found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => {
                    const chip = typeChip[customer.type] || typeChip.new;
                    return (
                      <TableRow key={`${customer.email}-${customer.phoneNumber}`}>
                        <TableCell sx={{ fontSize: 14, color: "#101828" }}>
                          {[customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
                            "—"}
                          {customer.email && (
                            <Box display="flex" alignItems="center" gap="4px">
                              <Typography fontSize={12} color="#98A2B3">
                                {customer.email}
                              </Typography>
                              <CopyButton value={customer.email} label="Email" />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, color: "#475467", whiteSpace: "nowrap" }}>
                          {customer.phoneNumber ? (
                            <Box display="flex" alignItems="center" gap="4px">
                              {customer.phoneNumber}
                              <CopyButton value={customer.phoneNumber} label="Phone number" />
                            </Box>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={chip.label}
                            sx={{
                              borderRadius: "6px",
                              fontSize: 12,
                              fontWeight: 600,
                              bgcolor: chip.bgcolor,
                              color: chip.color,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontSize: 14, fontWeight: 600, color: "#101828" }}
                        >
                          {customer.totalBookings}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, color: "#475467", whiteSpace: "nowrap" }}>
                          {customer.lastRoute || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, color: "#2F6B4F", whiteSpace: "nowrap" }}>
                          {customer.lastPnr || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, color: "#475467", whiteSpace: "nowrap" }}>
                          {formatDate(customer.firstBookingDate)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, color: "#475467", whiteSpace: "nowrap" }}>
                          {formatDate(customer.lastBookingDate)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalItems > 0 && (
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              gap="12px"
              mt="20px"
            >
              <Box display="flex" alignItems="center" gap="8px">
                <Typography fontSize={14} color="#475467">
                  Rows per page
                </Typography>
                <Select
                  size="small"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  sx={{ fontSize: 14, "& .MuiSelect-select": { py: "6px" } }}
                >
                  {PAGE_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
                <Typography fontSize={14} color="#98A2B3">
                  {Math.min((page - 1) * limit + 1, totalItems)}–
                  {Math.min(page * limit, totalItems)} of {totalItems.toLocaleString()}
                </Typography>
              </Box>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  size="small"
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomersTable;
